import { message } from "antd";
import { post } from "../utils/Backend";
import { resolveAppMediaUrl } from "../utils/resolveAppMediaUrl";
import {
  mockFetchAllBasicStocks,
  mockFetchOverseasBasicStocks,
  mockGetRecentSpaceVisits,
  mockGetUserByToken,
  mockLoginByPhoneCode,
  mockLogoutApi,
  mockSendLoginVerificationCode,
  useLongfansMock,
} from "./GlobalMock";
import {
  IS_STANDALONE,
  STANDALONE_SPACE_ID_CONFIG_FILE,
  STANDALONE_SPACE_ID_ENV_KEY,
  getStandaloneSpaceId,
} from "../standalone/config";
import type {
  AppComplianceLevelInfo,
  ComplianceLevelCode,
  CurrentUser,
  RecentSpaceEntry,
  StockSearchBrief,
  UserInfo,
} from "./GlobalType";

const LIST_ALL_BASIC_STOCK_BRIEFS_URL = "/lark/app/stock/listAllBasicStockBriefs";
const LIST_OVERSEAS_STOCK_BRIEFS_URL = "/lark/app/stock/listOverseasStockBriefs";

const rawPlatformUserAccountSpaceId =
  process.env.REACT_APP_USER_ACCOUNT_SPACE_ID?.trim() ?? "";

function resolveLoginSpaceId(): string {
  if (IS_STANDALONE) {
    return getStandaloneSpaceId();
  }
  return rawPlatformUserAccountSpaceId;
}

function convertToUserInfo(currentUser: CurrentUser): UserInfo {
  const props = currentUser.userProps;
  const id = String(
    (props as Record<string, unknown>).userId ??
      (props as Record<string, unknown>).id ??
      ""
  );
  const avatarRaw = (props as Record<string, unknown>).avatarUrl;
  const avatarUrl =
    typeof avatarRaw === "string" && avatarRaw.trim() !== ""
      ? resolveAppMediaUrl(avatarRaw.trim())
      : undefined;
  return {
    id,
    name: String(
      (props as Record<string, unknown>).nickName ??
        (props as Record<string, unknown>).name ??
        ""
    ),
    phone: String(
      (props as Record<string, unknown>).mobile ??
        (props as Record<string, unknown>).phoneNo ??
        ""
    ),
    avatarUrl,
    birthday:
      typeof (props as Record<string, unknown>).birthday === "string"
        ? String((props as Record<string, unknown>).birthday).trim() || undefined
        : undefined,
  };
}

export { filterBasicStocksByKeyword } from "fin-web-components";

/**
 * 根据 token 拉取当前用户（与 lark-ops CommonApi.getUserByToken 路径一致，见 表示层编写规范）
 */
const getUserByToken = async (): Promise<UserInfo | null> => {
  if (useLongfansMock) {
    if (!localStorage.getItem("token")) {
      return null;
    }
    const mock = mockGetUserByToken();
    if (!mock?.token) {
      return null;
    }
    return convertToUserInfo(mock);
  }
  const res = await post<Record<string, never>, CurrentUser>(
    "/lark/app/user/getUserInfo",
    {},
    { silent: true }
  );
  if (typeof res === "string" || !res) {
    return null;
  }
  if (res.token) {
    localStorage.setItem("token", res.token);
  }
  return convertToUserInfo(res);
};

/**
 * 与 lark-ops 一致：parameter.phoneNo。应用层实现见 lark-application AppAuthController。
 */
const sendLoginVerificationCode = async (
  phoneNo: string,
  captchaToken?: string,
): Promise<boolean> => {
  if (useLongfansMock) {
    return mockSendLoginVerificationCode(phoneNo);
  }
  const res = await post<
    { phoneNo: string; captchaToken?: string },
    boolean
  >(
    "/lark/app/sendLoginVerificationCode",
    {
      phoneNo,
      ...(captchaToken ? { captchaToken } : {}),
    },
    { silent: true },
  );
  if (typeof res === "string") {
    if (res === "1006") {
      message.error("发送过于频繁，请稍后再试");
    } else if (res === "1009") {
      message.error("请先完成滑动验证");
    } else if (res === "1010") {
      message.error("滑动验证已失效，请重试");
    }
    return false;
  }
  return !!res;
};

/**
 * 手机+验证码；平台站 extraInfo.spaceId 见 .env.longfans；
 * 独立站见 src/site-config/SiteConfig.ts 中 standaloneSite.spaceId。
 */
const loginByPhoneCode = async (
  phoneNo: string,
  code: string
): Promise<UserInfo | null> => {
  if (useLongfansMock) {
    const m = mockLoginByPhoneCode(phoneNo, code);
    if (!m) {
      return null;
    }
    localStorage.setItem("token", m.token);
    return convertToUserInfo(m);
  }
  let loginSpaceId = "";
  try {
    loginSpaceId = resolveLoginSpaceId();
  } catch {
    message.warning(
      `请在 ${STANDALONE_SPACE_ID_CONFIG_FILE} 中配置 ${STANDALONE_SPACE_ID_ENV_KEY}（8 位数字）`
    );
    return null;
  }
  if (
    !loginSpaceId ||
    (!/^\d{8}$/.test(loginSpaceId) &&
      !/^[0-9A-Za-z]{3}$/.test(loginSpaceId))
  ) {
    message.warning(
      IS_STANDALONE
        ? `请在 ${STANDALONE_SPACE_ID_CONFIG_FILE} 中配置 ${STANDALONE_SPACE_ID_ENV_KEY}（8 位数字）`
        : "请在 .env.longfans 中配置 REACT_APP_USER_ACCOUNT_SPACE_ID（3 位字母数字）"
    );
    return null;
  }
  const res = await post<
    { phoneNo: string; code: string; extraInfo: { spaceId: string } },
    CurrentUser
  >("/lark/app/user/login", {
    phoneNo,
    code: code.trim(),
    extraInfo: { spaceId: loginSpaceId },
  });
  if (typeof res === "string" || !res?.token) {
    return null;
  }
  localStorage.setItem("token", res.token);
  return convertToUserInfo(res);
};

const logoutApi = async (): Promise<void> => {
  if (useLongfansMock) {
    await mockLogoutApi();
    return;
  }
  await post<Record<string, never>, boolean>("/lark/app/user/logout", {}, { silent: true });
};

/**
 * 股票基础表全量简要（登录后由 GlobalModel 拉取；下拉只做 filterBasicStocksByKeyword）。
 */
const fetchAllBasicStocks = async (): Promise<StockSearchBrief[]> => {
  if (useLongfansMock) {
    return mockFetchAllBasicStocks();
  }
  const res = await post<Record<string, never>, StockSearchBrief[]>(
    LIST_ALL_BASIC_STOCK_BRIEFS_URL,
    {},
    { silent: true }
  );
  if (typeof res === "string") {
    return [];
  }
  return res ?? [];
};

const fetchOverseasBasicStocks = async (): Promise<StockSearchBrief[]> => {
  if (useLongfansMock) {
    return mockFetchOverseasBasicStocks();
  }
  const res = await post<Record<string, never>, StockSearchBrief[]>(
    LIST_OVERSEAS_STOCK_BRIEFS_URL,
    {},
    { silent: true }
  );
  if (typeof res === "string") {
    return [];
  }
  return res ?? [];
};

/**
 * 用户可访问的全部空间，按最近访问时间降序；未访问过的排在后段。
 * 顶栏仅展示前 3 条，其余从「我的关注」页进入。
 */
const getRecentSpaceVisits = async (): Promise<RecentSpaceEntry[]> => {
  if (useLongfansMock) {
    return mockGetRecentSpaceVisits();
  }
  const res = await post<Record<string, never>, RecentSpaceEntry[]>(
    "/lark/app/user/recentSubscribedSpaceVisits",
    {},
    { silent: true }
  );
  if (typeof res === "string" || !Array.isArray(res)) {
    return [];
  }
  return res;
};

const URL_COMPLIANCE_LEVEL =
  process.env.REACT_APP_COMPLIANCE_LEVEL_URL?.trim() ||
  "/lark/app/config/getComplianceLevel";

const getComplianceLevel = async (): Promise<ComplianceLevelCode> => {
  if (useLongfansMock) {
    return "finance_share";
  }
  const res = await post<Record<string, never>, AppComplianceLevelInfo>(
    URL_COMPLIANCE_LEVEL,
    {},
    { silent: true }
  );
  if (typeof res === "string" || !res?.levelCode) {
    return "finance_share";
  }
  return res.levelCode;
};

export const GlobalApi = {
  getComplianceLevel,
  getRecentSpaceVisits,
  fetchAllBasicStocks,
  fetchOverseasBasicStocks,
  sendLoginVerificationCode,
  loginByPhoneCode: (phone: string, code: string): Promise<UserInfo | null> =>
    loginByPhoneCode(phone, code),
  getUserByToken,
  logoutApi,
};
